import type { CheckPasswordStrengthResponse } from '@compass/client/types/auth';
import { AlertTriangle, Lightbulb, Loader2 } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  strength: CheckPasswordStrengthResponse | null;
  isChecking: boolean;
  error?: string | null;
  className?: string;
}

// Total number of segments in the strength bar. We surface four levels to the
// user (Very weak / Weak / Fair / Strong) rather than zxcvbn's five raw scores.
const STRENGTH_LEVELS = 4;

/**
 * Collapse zxcvbn's 0-4 score into one of four UI levels (0-3).
 *
 * We intentionally fold zxcvbn score 3 down into "Fair" rather than calling it
 * "Strong": a password can score 3 yet still fail our requirements (e.g. it
 * contains personal/page data), so labelling it "Strong" while the Reset
 * Password button stays disabled is misleading. Only the top zxcvbn score (4)
 * earns the "Strong" label.
 */
function getStrengthLevel(score: number | undefined): number {
  switch (score) {
    case 4:
      return 3; // Strong
    case 3:
    case 2:
      return 2; // Fair
    case 1:
      return 1; // Weak
    case 0:
    default:
      return 0; // Very weak
  }
}

function getStrengthColor(level: number): string {
  switch (level) {
    case 3:
      return 'bg-emerald-500';
    case 2:
      return 'bg-amber-500';
    case 1:
      return 'bg-orange-500';
    case 0:
    default:
      return 'bg-red-500';
  }
}

function getStrengthLabel(level: number): string {
  switch (level) {
    case 3:
      return 'Strong';
    case 2:
      return 'Fair';
    case 1:
      return 'Weak';
    case 0:
    default:
      return 'Very weak';
  }
}

function getStrengthTextColor(level: number): string {
  switch (level) {
    case 3:
      return 'text-emerald-500';
    case 2:
      return 'text-amber-500';
    case 1:
      return 'text-orange-500';
    case 0:
    default:
      return 'text-red-500';
  }
}

export function PasswordStrengthIndicator({
  strength,
  isChecking,
  error,
  className,
}: PasswordStrengthIndicatorProps) {
  if (error) {
    return <div className={cn('text-xs text-destructive', className)}>{error}</div>;
  }

  // Show loading state only if we don't have any strength data yet
  if (isChecking && !strength) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex justify-between items-center text-xs">
          <span className="text-muted-foreground">Password strength</span>
          <span className="text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
          </span>
        </div>
        <div className="flex gap-1">
          {[...Array(STRENGTH_LEVELS)].map((_, i) => (
            <div key={i} className="h-1.5 flex-1 rounded-full bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!strength) {
    return null;
  }

  // The submit button is gated on meets_requirements, which the API returns
  // ONLY when true — the proto field is `omitempty`, so a not-yet-acceptable
  // password arrives as `undefined`, NOT `false`. Treat anything other than an
  // explicit `true` as "not met", matching the button gate. (Comparing
  // `=== false` here missed the `undefined` case, which is why the CTA could be
  // disabled with no visible reason.)
  const requirementsMet = strength.meets_requirements === true;

  const rawLevel = getStrengthLevel(strength.score ?? 0);
  // Never present "Strong" while the password fails our requirements — e.g. a
  // top-scoring (zxcvbn 4) password that is breached (is_pwned) or contains
  // personal/page data. The submit button is gated on meets_requirements, so a
  // "Strong" label next to a disabled button is misleading. Cap the displayed
  // level at "Fair" until requirements are met (mirrors the score-3 fold above).
  const level = requirementsMet ? rawLevel : Math.min(rawLevel, 2);
  // Levels are 0-3, so we add 1 to get 1-4 filled bars.
  const filledBars = level + 1;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Strength label or loading indicator */}
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground">Password strength</span>
        {isChecking ? (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        ) : (
          <span className={getStrengthTextColor(level)}>{getStrengthLabel(level)}</span>
        )}
      </div>

      {/* Segmented strength bar */}
      <div className="flex gap-1">
        {[...Array(STRENGTH_LEVELS)].map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-all duration-300',
              i < filledBars ? getStrengthColor(level) : 'bg-muted',
            )}
          />
        ))}
      </div>

      {/* Pwned-password warning (shown when HIBP found a match). This takes
          priority over the zxcvbn warning since a breached password must be
          changed regardless of how strong it looks. */}
      {strength.is_pwned && (
        <div role="alert" className="flex items-start gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            This password has appeared in a known data breach (not necessarily from this site).
            Choose a different password to keep your account safe.
          </span>
        </div>
      )}

      {/* zxcvbn-derived suggestion: the single most important weakness. This is
          guidance for improving the password, not a hard error, so it uses a
          bulb icon and a warning (amber) tone rather than the destructive red. */}
      {!strength.is_pwned && strength.warning && (
        <div className="flex items-start gap-1.5 text-xs text-amber-600">
          <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{strength.warning}</span>
        </div>
      )}

      {/* Actionable suggestions for strengthening the password. Shown while the
          password is below the minimum bar so the user knows what to fix.
          Hidden once requirements are met to avoid nagging. */}
      {!strength.is_pwned &&
        !requirementsMet &&
        strength.suggestions &&
        strength.suggestions.length > 0 && (
          <ul className="space-y-0.5 text-xs text-muted-foreground">
            {strength.suggestions.map(suggestion => (
              <li key={suggestion} className="flex items-start gap-1.5">
                <span aria-hidden className="mt-0.5 shrink-0">
                  •
                </span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        )}

      {/* Fallback hint: requirements unmet but the API returned no specific
          warning or suggestions (e.g. a "Fair" password just below the bar).
          Give an actionable nudge — length is the biggest lever on the score
          that gates the submit button. */}
      {!strength.is_pwned &&
        !requirementsMet &&
        !strength.warning &&
        (!strength.suggestions || strength.suggestions.length === 0) && (
          <div className="text-xs text-muted-foreground">
            Not strong enough yet — try a longer password, or add more words and character variety.
          </div>
        )}
    </div>
  );
}
